import {
  DestroyRef,
  Directive,
  ElementRef,
  NgZone,
  OnInit,
  inject,
  signal,
} from '@angular/core';

/**
 * Publishes whether the host element's text is actually being clipped, so a
 * tooltip can be suppressed on the rows that show their value in full.
 *
 * Pair it with PrimeNG's `[tooltipDisabled]` via `exportAs`:
 *
 * ```html
 * <span
 *   appOverflowTooltip
 *   #clamp="overflowTooltip"
 *   class="w-full line-clamp-2"
 *   [pTooltip]="item.fileName"
 *   [tooltipDisabled]="!clamp.isOverflowing()"
 * >
 *   {{ item.fileName }}
 * </span>
 * ```
 *
 * **Why this measures continuously instead of on hover.** Measuring in a
 * `mouseenter` handler is the obvious implementation and it does not work:
 * PrimeNG's Tooltip reads `tooltipDisabled` inside its own `mouseenter`, so a
 * signal written from a competing handler would not reach the binding until the
 * next change-detection pass — one cycle after the tooltip has already decided
 * to show. Keeping the verdict current ahead of the hover sidesteps the race
 * entirely.
 *
 * Both axes are checked so the directive works on a single-line `truncate` host
 * (horizontal clip) as well as a `line-clamp-N` host (vertical clip). The 1px
 * tolerance absorbs sub-pixel rounding, which otherwise reports a permanent
 * 0.5px "overflow" on some zoom levels and font scales.
 */
@Directive({
  selector: '[appOverflowTooltip]',
  standalone: true,
  exportAs: 'overflowTooltip',
})
export class OverflowTooltipDirective implements OnInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  /** `true` only while the host is clipping its content. */
  readonly isOverflowing = signal(false);

  ngOnInit(): void {
    // Both observers live outside Angular's zone: they fire on every layout
    // pass of every row, and re-entering for a verdict that has not changed
    // would run change detection across the whole table for nothing.
    this.zone.runOutsideAngular(() => {
      // ResizeObserver fires once on observe(), which doubles as the initial
      // measurement — the host is laid out by then, whereas ngOnInit is not.
      // It then covers column resizes and viewport/breakpoint changes.
      const resizeObserver = new ResizeObserver(() => this.remeasure());
      resizeObserver.observe(this.host.nativeElement);

      // A row can be re-bound to a longer value without changing size (sort,
      // filter, paginate), which ResizeObserver alone would miss.
      const mutationObserver = new MutationObserver(() => this.remeasure());
      mutationObserver.observe(this.host.nativeElement, {
        characterData: true,
        childList: true,
        subtree: true,
      });

      this.destroyRef.onDestroy(
        () => {
          resizeObserver.disconnect();
          mutationObserver.disconnect();
        }
      );
    });
  }

  /** Exposed for specs, which cannot wait on a real observer callback. */
  remeasure(): void {
    const el = this.host.nativeElement;
    const clipped =
      el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1;

    if (clipped === this.isOverflowing())
        return;

    // Re-enter the zone only on a flip, so the binding updates.
    this.zone.run(() => this.isOverflowing.set(clipped));
  }
}
